<script>
import { mapGetters, mapMutations } from 'vuex/dist/vuex.common';
import { wizardEmitterMixin } from '@/mixins/wizard';
import { LOADING_KEY } from '@/util/constants';

import EzButton from '@/components/ui/Button';
import EzWizard from '@/components/layout/Wizard.vue';
import EzLoader from '@/components/ui/Loader/EzLoader';
import steps, { TRANSFER_STEP_1, TRANSFER_STEP_2, TRANSFER_STEP_3 } from './steps';

/**
 * Inventory Transfer Flow
 * @version 1.0.0
 * @since 3.18.0
 */

export default {
  mixins: [wizardEmitterMixin],
  components: { EzButton, EzWizard, EzLoader },
  data() {
    return {
      TRANSFER_STEP_1,
      TRANSFER_STEP_2,
      TRANSFER_STEP_3,
      currentStep: 0,
      loadingMsg: 'Loading...',
      nextDisabled: true,
    };
  },
  computed: {
    ...mapGetters('loading', ['isSomeLoading']),
    isLoading() {
      return this.isSomeLoading([LOADING_KEY.FETCH_INVENTORY_TRANSFER_DESTINATIONS]);
    },
    currentStepComponent() {
      return steps[this.currentStep].component;
    },
    currentStepPageTitle() {
      return steps[this.currentStep].pageTitle;
    },
    currentStepPageInfo() {
      return steps[this.currentStep].pageInfo;
    },
    nextCta() {
      return steps[this.currentStep].nextCta;
    },
  },
  methods: {
    ...mapMutations('entities/orders', [
      'CLEAR_DRAFT',
      'CLEAR_PRODUCTS',
      'CLEAR_SELECTED_PRODUCTS',
    ]),
    clearState() {
      this.CLEAR_DRAFT();
      this.CLEAR_PRODUCTS();
      this.CLEAR_SELECTED_PRODUCTS();
    },
    onWizardExit() {
      this.clearState();
      const mediaQuery = window.matchMedia('(max-width: 768px)');
      if (mediaQuery.matches) {
        return this.$router.push({ name: 'platform-home' });
      }
      return this.$router.push({ name: 'venue-stock-all' });
    },
    onStepBack(step) {
      if (step !== undefined && step >= TRANSFER_STEP_1 && step <= TRANSFER_STEP_3) {
        this.currentStep = step;
        return;
      }

      if (this.currentStep <= TRANSFER_STEP_1) return;

      this.currentStep -= 1;
    },
    onStepCompleted() {
      if (this.currentStep >= TRANSFER_STEP_3) {
        return;
      }

      this.nextDisabled = true;
      this.currentStep += 1;
    },
  },
  created() {
    this.currentStep = TRANSFER_STEP_1;
  },
  beforeDestroy() {
    this.clearState();
  },
};
</script>

<template>
  <ez-wizard
    @exit="onWizardExit"
    @back="goToPreviousStep"
    :extraWide="[TRANSFER_STEP_2, TRANSFER_STEP_3].includes(currentStep)"
  >
    <template #title>Inventory Transfer</template>
    <template #nav>
      <ul>
        <li
          :class="{
            'ez-step-active': currentStep === TRANSFER_STEP_1,
            'ez-step-completed': currentStep > TRANSFER_STEP_1,
          }"
        >
          1. Outlet
        </li>
        <li
          :class="{
            'ez-step-active': currentStep === TRANSFER_STEP_2,
            'ez-step-completed': currentStep > TRANSFER_STEP_2,
          }"
        >
          2. Products
        </li>
      </ul>
    </template>
    <template #actions>
      <ez-button :disabled="nextDisabled" @click="goToNextStep">
        {{ nextCta }}
      </ez-button>
    </template>
    <template #prevStep>
      <ez-button
        v-if="[TRANSFER_STEP_2, TRANSFER_STEP_3].includes(currentStep)"
        type="link"
        formType="button"
        @click="goToPreviousStep"
      >
        <font-awesome-icon icon="arrow-left" />
        <span>Back</span>
      </ez-button>
    </template>
    <template #pageTitle>{{ currentStepPageTitle }}</template>
    <template #pageInfo>
      <div v-html="currentStepPageInfo"></div>
    </template>
    <component :is="currentStepComponent" @stepCompleted="onStepCompleted" @stepBack="onStepBack" />
    <ez-loader :show="isLoading">
      {{ loadingMsg }}
    </ez-loader>
  </ez-wizard>
</template>
